import { useState, useEffect } from 'react';
import { getConversations } from '../api/conversations';
import { useSocket } from '../hooks/useSocket';
import { Topbar } from '../layout/Topbar';
import type { Conversation, Message } from '../types';

function MetricCard({ label, value, subtitle, color }: {
  label: string;
  value: number;
  subtitle: string;
  color: string;
}) {
  return (
    <div className="metric-card">
      <span className="label">{label}</span>
      <span className="value" style={{ color }}>{value}</span>
      <span className="subtitle">{subtitle}</span>
    </div>
  );
}

export function DashboardPage() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const { on } = useSocket();

  useEffect(() => {
    getConversations()
      .then(setConversations)
      .catch(() => console.error('Falha ao carregar conversas no dashboard'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const unsub1 = on('conversation.created', (conv: any) => {
      setConversations((prev: Conversation[]) => [conv, ...prev]);
    });
    const unsub2 = on('conversation.status_changed', (conv: any) => {
      setConversations((prev: Conversation[]) => prev.map((c) => (c.id === conv.id ? { ...c, ...conv } : c)));
    });
    const unsub3 = on('message.created', (msg: any) => {
      setConversations((prev: Conversation[]) =>
        prev.map((c) =>
          c.id === msg.conversationId
            ? { ...c, messages: [...c.messages, msg], lastMessageAt: msg.createdAt }
            : c,
        ),
      );
    });
    return () => { unsub1(); unsub2(); unsub3(); };
  }, [on]);

  const metrics = {
    open: conversations.filter((c) => c.status !== 'CLOSED').length,
    waiting: conversations.filter((c) => c.status === 'WAITING_AGENT').length,
    assigned: conversations.filter((c) => c.status === 'ASSIGNED').length,
    bot: conversations.filter((c) => c.status === 'BOT').length,
    leads: conversations.filter((c) => c.customer?.isLead).length,
  };

  const deptCount: Record<string, number> = {};
  conversations
    .filter((c) => c.status === 'WAITING_AGENT' && c.department)
    .forEach((c) => {
      const name = c.department!.name;
      deptCount[name] = (deptCount[name] || 0) + 1;
    });

  const recentMessages = conversations
    .flatMap((c) =>
      (c.messages || [])
        .filter((m) => m.senderType === 'CUSTOMER')
        .map((m) => ({ ...m, customerName: c.customer?.name || 'Cliente' })),
    )
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 10);

  if (loading) {
    return (
      <div className="view-container">
        <Topbar title="Painel operacional" subtitle="Visão geral do atendimento, filas e automações." />
        <div className="empty-state">
          <div className="spinner" />
          <p>Carregando dados...</p>
        </div>
      </div>
    );
  }

  const deptColors = ['#00897b', '#1565c0', '#f57c00', '#2e7d32'];

  return (
    <div className="view-container">
      <Topbar title="Painel operacional" subtitle="Visão geral do atendimento, filas e automações." />

      <div className="metrics-grid">
        <MetricCard label="Conversas ativas" value={metrics.open} subtitle="Em andamento" color="#1565c0" />
        <MetricCard label="Aguardando agente" value={metrics.waiting} subtitle="Fila de atendimento" color="#f57c00" />
        <MetricCard label="Atendimentos em andamento" value={metrics.assigned} subtitle="Assumidos por agentes" color="#00897b" />
        <MetricCard label="Leads de vendas" value={metrics.leads} subtitle="Clientes não localizados" color="#d32f2f" />
      </div>

      <div className="dashboard-grid">
        <div className="panel">
          <div className="panel-header">
            <h2>Filas por setor</h2>
            <span className="text-sm text-secondary">Distribuição atual</span>
          </div>
          <div className="panel-body">
            {Object.entries(deptCount).length === 0 ? (
              <div className="empty-state">
                <p>Todas as filas estão vazias no momento.</p>
              </div>
            ) : (
              Object.entries(deptCount).map(([dept, count], i) => (
                <div
                  key={dept}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-sm) 0',
                    borderBottom: '1px solid var(--border-light)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: 'var(--radius-sm)',
                        background: deptColors[i % deptColors.length],
                      }}
                    />
                    <span style={{ fontWeight: 500 }}>{dept}</span>
                  </div>
                  <span className="pill primary">{count} aguardando</span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="panel">
          <div className="panel-header">
            <h2>Últimas mensagens</h2>
            <span className="text-sm text-secondary">Atividade recente dos clientes</span>
          </div>
          <div className="panel-body" style={{ padding: 0 }}>
            {recentMessages.length === 0 ? (
              <div className="empty-state">
                <p>Nenhuma atividade recente.</p>
              </div>
            ) : (
              recentMessages.map((msg, i) => (
                <div
                  key={msg.id || i}
                  style={{
                    padding: 'var(--space-sm) var(--space-md)',
                    borderBottom: '1px solid var(--border-light)',
                    fontSize: '0.857rem',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                    <strong>{msg.customerName}</strong>
                    <span style={{ color: 'var(--text-tertiary)', fontSize: '0.714rem' }}>
                      {new Date(msg.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                    {msg.content.slice(0, 120)}{msg.content.length > 120 ? '...' : ''}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
